import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, ConfirmModal, CustomSelect, Button, BulkSelectCell, ListSearchInput, ListPagination } from '@redpanda/ui';
import { useBookings, Booking } from '../../stores/useBookingStore';
import { useServiceStore } from '../../stores/useServiceStore';
import { useStaffStore } from '../../stores/useStaffStore';
import { useCurrentStore } from '../../hooks/useCurrentStore';
import { useBulkActions, useListSearch, useRememberedState, emitDataChanged } from '@redpanda/utils';
import { bookingApi } from '../../services/api';
import toast from 'react-hot-toast';
import BookingDialog from '../../components/BookingDialog';
import BookingLimitBanner from '../../components/BookingLimitBanner';
import MultiBulkActionButton from '../../components/BulkOperations/MultiBulkActionButton';
import BulkSelectHeader from '../../components/BulkOperations/BulkSelectHeader';
import { TableSkeleton } from '@redpanda/ui';
import StatusBadgeDropdown from '../../components/StatusBadgeDropdown';

const BOOKING_STATUSES = ['PENDING', 'CONFIRMED', 'COMPLETED', 'CANCELLED'] as const;

/** 年份＋月份 → 查詢的日期區間（本地時間）：只有年份＝整年 */
const dateRangeOf = (year: string, month: string): { startDate?: string; endDate?: string } => {
  if (!year) return {};
  const y = Number(year);
  const m = month ? Number(month) - 1 : null;
  const start = new Date(y, m ?? 0, 1);
  const end = m === null ? new Date(y + 1, 0, 1) : new Date(y, m + 1, 1);
  end.setMilliseconds(-1); // 最後一天 23:59:59.999
  return { startDate: start.toISOString(), endDate: end.toISOString() };
};

const BookingsPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useCurrentStore();

  // 生成年份選項（比照發票管理）
  const generateYearOptions = (): Array<{ value: string; label: string }> => {
    const currentYear = new Date().getFullYear();
    const yearOptions = [];
    for (let i = 0; i < 6; i++) {
      const year = currentYear - i;
      yearOptions.push({
        value: year.toString(),
        label: t('pages:common.time.year', { year })
      });
    }
    return yearOptions;
  };

  // 生成月份選項（比照發票管理）
  const generateMonthOptions = (): Array<{ value: string; label: string }> => {
    return [
      { value: '1', label: t('pages:common.time.month', { month: 1 }) },
      { value: '2', label: t('pages:common.time.month', { month: 2 }) },
      { value: '3', label: t('pages:common.time.month', { month: 3 }) },
      { value: '4', label: t('pages:common.time.month', { month: 4 }) },
      { value: '5', label: t('pages:common.time.month', { month: 5 }) },
      { value: '6', label: t('pages:common.time.month', { month: 6 }) },
      { value: '7', label: t('pages:common.time.month', { month: 7 }) },
      { value: '8', label: t('pages:common.time.month', { month: 8 }) },
      { value: '9', label: t('pages:common.time.month', { month: 9 }) },
      { value: '10', label: t('pages:common.time.month', { month: 10 }) },
      { value: '11', label: t('pages:common.time.month', { month: 11 }) },
      { value: '12', label: t('pages:common.time.month', { month: 12 }) }
    ];
  };
  const {
    bookings,
    pagination,
    isLoading,
    isDeleting,
    showCancelConfirmModal,
    showDeleteConfirmModal,
    fetchBookings,
    setCurrentPage,
    refreshData,
    createBooking,
    updateBooking,
    handleCancel,
    confirmCancel,
    closeCancelModal,
    handleDelete: handleDeleteSingle,
    confirmDelete: confirmDeleteSingle,
    closeDeleteModal,
  } = useBookings();

  const fetchServices = useServiceStore((state) => state.fetchServices);
  const fetchStaff = useStaffStore((state) => state.fetchStaff);

  // 對話框狀態
  const [showDialog, setShowDialog] = useState(false);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);

  // 預約數量限制
  const [canAddBooking, setCanAddBooking] = useState(true);
  const [bookingLimitRefreshTrigger, setBookingLimitRefreshTrigger] = useState(0);

  // 搜尋、篩選由後端查全部預約（原本只過濾目前這一頁，第 2 頁以後搜不到）
  // 狀態篩選記住上次的選擇；搜尋文字、年月（日期）不記住；月份要先選年份
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedStatus, setSelectedStatus] = useRememberedState('booking-ui:bookings.status', '', ['', ...BOOKING_STATUSES]);
  const changeYear = (year: string) => {
    setSelectedYear(year);
    if (!year) setSelectedMonth('');
  };
  const search = searchTerm.trim();
  const hasFilters = !!(search || selectedYear || selectedStatus);
  const filterQuery = { search: search || undefined, ...dateRangeOf(selectedYear, selectedMonth), status: selectedStatus || undefined };
  const searchResult = useListSearch<Booking>(
    'bookings',
    hasFilters ? filterQuery : null,
    page => bookingApi.getPaginated(page, 20, filterQuery),
    () => toast.error(t('translation:common.loadFailed', '載入失敗'))
  );
  const filteredBookings = searchResult.active ? searchResult.items : (Array.isArray(bookings) ? bookings : []);
  const displayPagination = searchResult.active ? searchResult.pagination : pagination;

  // 權限檢查：只有已取消的預約可以刪除
  const canDeleteBooking = (booking: Booking): boolean => {
    return booking.status === 'CANCELLED';
  };

  // 批量操作（比照發票管理）
  const bulkActions = useBulkActions<Booking>({
    items: filteredBookings,
    idField: 'id',
    entityName: t('translation:common.booking'),
    bulkDeleteApi: bookingApi.bulkDelete,
    canDeleteItem: canDeleteBooking,
    toast,
    pagination: displayPagination ? {
      currentPage: displayPagination.currentPage,
      totalPages: displayPagination.totalPages,
    } : undefined,
    // 批次刪除後會呼叫 fetchItems 重抓列表：搜尋結果也一起重抓
    fetchItems: (page?: number, force?: boolean) => {
      emitDataChanged('bookings');
      return fetchBookings(page, force);
    },
    setCurrentPage: setCurrentPage,
  });
  // 有搜尋、篩選時換頁走後端查詢結果
  const changePage = (page: number) => (searchResult.active ? searchResult.setPage(page) : bulkActions.handlePageChange(page));

  // 初次加載資料
  useEffect(() => {
    if (storeId) {
      fetchBookings(1);
      fetchServices(storeId);
      fetchStaff(storeId);
    }
  }, [storeId, fetchBookings, fetchServices, fetchStaff]);

  const handleAddClick = () => {
    setEditingBooking(null);
    setShowDialog(true);
  };

  const handleEditClick = (booking: Booking) => {
    setEditingBooking(booking);
    setShowDialog(true);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    setEditingBooking(null);
  };

  const handleDialogSubmit = async (data: any) => {
    if (!storeId) return;

    if (editingBooking) {
      // 編輯模式：updateBooking 已有樂觀更新，Dialog 會自行關閉
      await updateBooking(editingBooking.id, data);
      emitDataChanged('bookings');
    } else {
      // 新增模式：等待 API 完成後刷新資料
      await createBooking(storeId, data);
      handleDialogClose();
      refreshData();
      emitDataChanged('bookings');
      setBookingLimitRefreshTrigger(prev => prev + 1);
    }
  };

  // 快速切換狀態
  const handleStatusChange = async (bookingId: string, newStatus: string) => {
    try {
      await updateBooking(bookingId, { status: newStatus });
      emitDataChanged('bookings');
      // toast 和 store 更新已在 updateBooking 中處理
    } catch (error) {
      console.error('Failed to update status:', error);
      // 錯誤 toast 已在 updateBooking 中處理
    }
  };


  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 電話號碼格式化：顯示時加上 - 號（例：0912-592-953）
  const formatPhoneNumber = (phone: string | null | undefined): string => {
    if (!phone) return '';
    // 移除所有非數字字符
    const cleaned = phone.replace(/[^0-9]/g, '');
    // 格式化為 0912-592-953 格式（台灣手機號碼）
    if (cleaned.length === 10 && cleaned.startsWith('09')) {
      return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    // 如果不是標準格式，直接返回清理後的數字
    return cleaned;
  };

  // 狀態選項
  const statusOptions = [
    { value: '', label: t('translation:common.allStatus') },
    { value: 'PENDING', label: t('translation:bookingStatus.PENDING') },
    { value: 'CONFIRMED', label: t('translation:bookingStatus.CONFIRMED') },
    { value: 'COMPLETED', label: t('translation:bookingStatus.COMPLETED') },
    { value: 'CANCELLED', label: t('translation:bookingStatus.CANCELLED') },
  ];

  return (
    <div className="container mx-auto px-4 md:px-10 py-6 pb-32">
      {/* 標題區 */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {t('pages:admin.bookings.title')}
          </h1>
          <div className="flex items-baseline gap-3">
            {/* 批量操作按鈕 (桌面版) */}
            <div className="hidden md:block">
              <MultiBulkActionButton
                selectedCount={bulkActions.selectedItems.length}
                onDelete={bulkActions.handleBulkDelete}
                isDeleting={bulkActions.isBulkDeleting}
              />
            </div>
            <button
              onClick={handleAddClick}
              disabled={!canAddBooking}
              className={`hidden md:block font-bold py-2 px-4 rounded transition-all duration-200 ${
                canAddBooking
                  ? 'bg-brand-600 hover:bg-brand-700 text-white cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title={canAddBooking ? undefined : t('resourceLimit:limitReachedUpgrade', { entityName: t('translation:entityNames.booking') })}
            >
              {t('translation:common.add')}
            </button>
          </div>
        </div>
      </div>

      {/* 預約數量限制資訊 */}
      <BookingLimitBanner onLimitChange={setCanAddBooking} refreshTrigger={bookingLimitRefreshTrigger} />

      {/* 搜尋和篩選區域（比照發票管理） */}
      <div className="mb-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* 搜尋框 */}
          <ListSearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t('pages:admin.bookings.searchPlaceholder')} widthClass="" />

          {/* 年份篩選 */}
          <div>
            <CustomSelect
              value={selectedYear}
              onChange={changeYear}
              placeholder={t('translation:common.selectYear')}
              className="h-10"
              options={generateYearOptions()}
            />
          </div>

          {/* 月份篩選 */}
          <div>
            <CustomSelect
              value={selectedMonth}
              onChange={setSelectedMonth}
              disabled={!selectedYear}
              placeholder={t('translation:common.selectMonth')}
              className="h-10"
              options={generateMonthOptions()}
            />
          </div>

          {/* 狀態篩選 */}
          <div>
            <CustomSelect
              value={selectedStatus}
              onChange={setSelectedStatus}
              placeholder={t('translation:common.selectStatus')}
              className="h-10"
              options={statusOptions}
            />
          </div>

          {/* 清除篩選按鈕 */}
          <div className="flex flex-col space-y-2">
            <Button
              type="button"
              onClick={() => {
                setSearchTerm('');
                setSelectedYear('');
                setSelectedMonth('');
                setSelectedStatus('');
              }}
              variant="ghost"
              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md h-10 flex items-center justify-center"
            >
              {t('translation:common.clearFilters')}
            </Button>
          </div>
        </div>
      </div>

      {/* 批量操作按鈕 (手機版) */}
      <div className="md:hidden mb-4">
        <MultiBulkActionButton
          selectedCount={bulkActions.selectedItems.length}
          onDelete={bulkActions.handleBulkDelete}
          isDeleting={bulkActions.isBulkDeleting}
        />
      </div>

      {/* 手機版卡片列表 */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {t('translation:common.loading')}
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {searchTerm || selectedYear || selectedMonth || selectedStatus ? t('translation:common.noResults') : t('translation:common.noBookings')}
          </div>
        ) : (
          filteredBookings.map((booking) => {
            const canDelete = booking.status === 'CANCELLED';
            return (
              <div
                key={booking.id}
                className="bg-white dark:bg-slate-800 shadow rounded-lg p-3 space-y-3"
              >
                {/* 選擇框 */}
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    checked={bulkActions.isItemSelected(booking.id)}
                    onChange={() => bulkActions.toggleItemSelection(booking.id)}
                    disabled={!canDelete}
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 mt-1 mr-3"
                  />
                  <div className="flex-1">
                    {/* 客戶資訊 */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base font-semibold text-gray-900 dark:text-white">
                          {booking.customer.name}
                        </span>
                        <StatusBadgeDropdown
                          currentStatus={booking.status}
                          onStatusChange={(newStatus) => handleStatusChange(booking.id, newStatus)}
                        />
                      </div>
                      {booking.customer.phone && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {formatPhoneNumber(booking.customer.phone)}
                        </p>
                      )}
                    </div>

                    {/* 服務詳情 */}
                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3 mt-3 space-y-2">
                      <div className="flex items-center text-sm">
                        <span className="text-gray-500 dark:text-gray-400 w-16">{t('translation:common.service')}：</span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {booking.service.name}
                        </span>
                      </div>
                      <div className="flex items-center text-sm">
                        <span className="text-gray-500 dark:text-gray-400 w-16">{t('translation:common.staff')}：</span>
                        <span className="text-gray-900 dark:text-white">
                          {booking.staff?.name || t('translation:common.unassigned')}
                        </span>
                      </div>
                      <div className="flex items-center text-sm">
                        <span className="text-gray-500 dark:text-gray-400 w-16">{t('translation:common.timeLabel')}：</span>
                        <span className="text-gray-900 dark:text-white">
                          {formatDateTime(booking.date)}
                        </span>
                      </div>
                      <div className="flex items-center text-sm">
                        <span className="text-gray-500 dark:text-gray-400 w-16">{t('translation:common.amount')}：</span>
                        <span className="text-brand-600 dark:text-brand-400 font-semibold">
                          ${Math.floor(Number(booking.price))}
                        </span>
                      </div>
                    </div>

                    {/* 操作按鈕 */}
                    <div className="flex space-x-4 border-t border-gray-200 dark:border-slate-700 pt-3 mt-3">
                      <button
                        onClick={() => handleEditClick(booking)}
                        className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300 font-medium"
                      >
                        {t('translation:common.edit')}
                      </button>
                      {booking.status !== 'CANCELLED' && booking.status !== 'COMPLETED' && (
                        <button
                          onClick={() => handleCancel(booking.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 font-medium"
                        >
                          {t('translation:common.cancel')}
                        </button>
                      )}
                      {booking.status === 'CANCELLED' && (
                        <button
                          onClick={() => handleDeleteSingle(booking.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 font-medium"
                        >
                          {t('translation:common.delete')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 表格上方的分頁控制 */}
      <ListPagination pagination={displayPagination} onPageChange={changePage} className="mb-3" />

      {/* 桌面版表格列表 */}
      <div className="hidden md:block">
        {isLoading ? (
          <TableSkeleton rows={10} columns={8} />
        ) : (
          <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
            <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <Table style={{ tableLayout: 'fixed', minWidth: '1000px' }}>
                <TableHeader>
                  <TableRow>
                    <BulkSelectHeader
                      allSelected={bulkActions.allSelected}
                      onToggleAll={bulkActions.toggleSelectAll}
                      hasItems={filteredBookings.length > 0}
                    />
                    <TableHead className="w-[15%]">{t('translation:common.customer')}</TableHead>
                    <TableHead className="w-[15%]">{t('translation:common.service')}</TableHead>
                    <TableHead className="w-[10%]">{t('translation:common.staff')}</TableHead>
                    <TableHead className="w-[18%]">{t('translation:common.bookingTime')}</TableHead>
                    <TableHead className="w-[10%]">{t('translation:common.status')}</TableHead>
                    <TableHead className="w-[10%]">{t('translation:common.amount')}</TableHead>
                    <TableHead className="w-[12%]">{t('translation:common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500 dark:text-gray-400">
                        {searchTerm || selectedYear || selectedMonth || selectedStatus ? t('translation:common.noResults') : t('translation:common.noBookings')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBookings.map((booking) => {
                      const canDelete = booking.status === 'CANCELLED';
                      return (
                        <TableRow key={booking.id}>
                          <BulkSelectCell
                            isSelected={bulkActions.isItemSelected(booking.id)}
                            onToggle={() => bulkActions.toggleItemSelection(booking.id)}
                            canDelete={canDelete}
                            disabledReason={!canDelete ? t('translation:messages.canOnlyDeleteCancelled') : undefined}
                          />
                          <TableCell className="truncate" title={booking.customer.name}>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {booking.customer.name}
                              </div>
                              {booking.customer.phone && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatPhoneNumber(booking.customer.phone)}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="truncate" title={booking.service.name}>
                            {booking.service.name}
                          </TableCell>
                          <TableCell className="truncate" title={booking.staff?.name || t('translation:common.unassigned')}>
                            {booking.staff?.name || t('translation:common.unassigned')}
                          </TableCell>
                          <TableCell>{formatDateTime(booking.date)}</TableCell>
                          <TableCell>
                            <StatusBadgeDropdown
                              currentStatus={booking.status}
                              onStatusChange={(newStatus) => handleStatusChange(booking.id, newStatus)}
                            />
                          </TableCell>
                          <TableCell>${Math.floor(Number(booking.price))}</TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-4">
                              <button
                                onClick={() => handleEditClick(booking)}
                                className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300"
                              >
                                {t('translation:common.edit')}
                              </button>
                              {booking.status !== 'CANCELLED' && booking.status !== 'COMPLETED' && (
                                <button
                                  onClick={() => handleCancel(booking.id)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  {t('translation:common.cancel')}
                                </button>
                              )}
                              {booking.status === 'CANCELLED' && (
                                <button
                                  onClick={() => handleDeleteSingle(booking.id)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  {t('translation:common.delete')}
                                </button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* 分頁組件（比照發票管理） */}
      <ListPagination pagination={displayPagination} onPageChange={changePage} className="mt-6" />

      {/* 浮動操作按鈕（FAB）- 僅手機版顯示 */}
      <button
        onClick={handleAddClick}
        disabled={!canAddBooking}
        className={`md:hidden fixed bottom-20 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center z-40 transition-all duration-200 active:scale-95 ${
          canAddBooking
            ? 'bg-brand-600 hover:bg-brand-700 text-white'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
        }`}
        aria-label={t('translation:common.add')}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>

      {/* 新增/編輯對話框 */}
      <BookingDialog
        isOpen={showDialog}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
        booking={editingBooking}
      />

      {/* 取消確認對話框 */}
      {showCancelConfirmModal && (
        <ConfirmModal
          isOpen={showCancelConfirmModal}
          onClose={closeCancelModal}
          onConfirm={async () => {
            await confirmCancel();
            emitDataChanged('bookings');
          }}
          title={t('translation:common.cancelBooking')}
          message={t('translation:common.cancelBookingConfirm')}
          confirmText={t('translation:common.confirm')}
          cancelText={t('translation:common.cancel')}
          type="danger"
        />
      )}

      {/* 刪除確認對話框 (單筆) */}
      {showDeleteConfirmModal && (
        <ConfirmModal
          isOpen={showDeleteConfirmModal}
          onClose={closeDeleteModal}
          onConfirm={async () => {
            await confirmDeleteSingle();
            emitDataChanged('bookings');
          }}
          title={t('translation:common.deleteBooking')}
          message={t('translation:common.deleteBookingConfirm')}
          confirmText={t('translation:common.delete')}
          cancelText={t('translation:common.cancel')}
          type="danger"
          isLoading={isDeleting}
        />
      )}

      {/* 批量刪除確認對話框 */}
      {bulkActions.showBulkDeleteModal && (
        <ConfirmModal
          isOpen={bulkActions.showBulkDeleteModal}
          onClose={bulkActions.closeBulkDeleteModal}
          onConfirm={bulkActions.confirmBulkDelete}
          title={t('translation:common.bulkDeleteBookings')}
          message={t('translation:common.bulkDeleteBookingsConfirm', { count: bulkActions.selectedItems.length })}
          confirmText={t('translation:common.delete')}
          cancelText={t('translation:common.cancel')}
          type="danger"
          isLoading={bulkActions.isBulkDeleting}
        />
      )}
    </div>
  );
};

export default BookingsPage;
